import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableTitleWrapper,
  SearchContainer,
  TableBoxesWrapper,
} from 'pages/Account/pages/Profile/Forms/tableStyles';
import { PageSearchInput } from '__components/Layout/PageToolbar';
import { APIKeysNotice } from 'pages/Account/pages/Profile/Forms/APIKeys/styles';
import { InfiniteTable } from './InfiniteTable';
import { KizenTypography } from '__app/typography';
import type { MenuAction, SelectedServiceAccount } from './types';
import {
  CreateServiceAccountModal,
  ViewApiTokenModal,
  RotateTokenModal,
  DeleteSecondaryTokenModal,
  // KZN-17001: Add support for 'disabled' status for service accounts
  // DisableServiceAccountModal,
  // EnableServiceAccountModal,
  ArchiveServiceAccountModal,
} from './ServiceAccountModals';
import { ServiceAccountHistoryModal } from './ServiceAccountHistoryModal';
import { StyledAddButton, StyledHeaderWrapper } from './styles';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { serviceAccountsInfiniteQuery } from 'query/service-account';
import { runToolsAsOptionsQuery } from 'query/automation';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import type { AccessStruct } from '@kizen/permissions/types';
import { PageCard, SectionWrapper } from '../styles';
import type { ServiceAccount } from '@kizen/api/service-account';

export const ServiceAccounts: FC<{
  access: {
    manageServiceAccounts: AccessStruct;
  };
}> = ({ access }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const serviceAccountAccess = access.manageServiceAccounts;

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SelectedServiceAccount>({
    id: '',
    data: null,
    action: null,
  });

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: serviceAccountsInfiniteQuery().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: runToolsAsOptionsQuery().queryKey,
    });
  }, [queryClient]);

  const onSelectAction = useCallback(
    (action: MenuAction, account: ServiceAccount | null) => {
      setSelected({
        id: account?.id ?? '',
        data: account,
        action,
      });
    },
    []
  );

  const isFetching = useIsFetching({
    exact: false,
    queryKey: serviceAccountsInfiniteQuery().queryKey,
  });

  return (
    <SectionWrapper>
      <ContentWidth>
        <PageCard data-qa="manage-connections__service-accounts">
          <TableTitleWrapper>
            <KizenTypography as="h3" type="subheader">
              {t('Service Accounts')}
            </KizenTypography>
            <StyledHeaderWrapper>
              {serviceAccountAccess?.edit ? (
                <StyledAddButton
                  variant="text"
                  onClick={() => onSelectAction('create', null)}
                  data-qa="add-service-account-btn"
                >
                  {t('+ Add Service Account')}
                </StyledAddButton>
              ) : null}
              <SearchContainer>
                <PageSearchInput
                  placeholder={t('Find Account')}
                  value={search}
                  onChange={setSearch}
                  loading={!!isFetching}
                  data-qa="service-accounts-search"
                />
              </SearchContainer>
            </StyledHeaderWrapper>
          </TableTitleWrapper>
          <TableBoxesWrapper>
            <APIKeysNotice>
              <KizenTypography as="div">
                {t(
                  'Service Accounts are the recommended way to integrate external services with your Kizen business. Service Accounts each can have distinct permissions and API keys, which can be rotated easily.'
                )}
              </KizenTypography>
            </APIKeysNotice>
          </TableBoxesWrapper>
          <InfiniteTable
            onSelectAction={onSelectAction}
            search={search}
            access={serviceAccountAccess}
          />
        </PageCard>
      </ContentWidth>

      {selected.action === 'create' ? (
        <CreateServiceAccountModal
          setSelectedServiceAccount={setSelected}
          refetch={refetch}
        />
      ) : null}
      {selected.action === 'view-token' ? (
        <ViewApiTokenModal
          selectedServiceAccount={selected}
          setSelectedServiceAccount={setSelected}
        />
      ) : null}
      {selected.action === 'rotate-token' ? (
        <RotateTokenModal
          selectedServiceAccount={selected}
          setSelectedServiceAccount={setSelected}
          refetch={refetch}
        />
      ) : null}
      {selected.action === 'delete-secondary-token' ? (
        <DeleteSecondaryTokenModal
          selectedServiceAccount={selected}
          setSelectedServiceAccount={setSelected}
          refetch={refetch}
        />
      ) : null}
      {/* KZN-17001: Add support for 'disabled' status for service accounts
      {selected.action === 'disable' ? (
        <DisableServiceAccountModal
          selectedServiceAccount={selected}
          setSelectedServiceAccount={setSelected}
          refetch={refetch}
        />
      ) : null}
      {selected.action === 'enable' ? (
        <EnableServiceAccountModal
          selectedServiceAccount={selected}
          setSelectedServiceAccount={setSelected}
          refetch={refetch}
        />
      ) : null}
      */}
      {selected.action === 'view-history' ? (
        <ServiceAccountHistoryModal
          id={selected.id}
          onHide={() =>
            setSelected({
              id: '',
              data: null,
              action: null,
            })
          }
        />
      ) : null}
      {selected.action === 'archive' ? (
        <ArchiveServiceAccountModal
          selectedServiceAccount={selected}
          setSelectedServiceAccount={setSelected}
          refetch={refetch}
        />
      ) : null}
    </SectionWrapper>
  );
};
